import { useCallback, useEffect, useState } from "react";
import { Hammer, Layers, Plus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, LoadingRows } from "@/components/shared";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const sampleVector = (dim) => Array.from({ length: dim }, (_, i) => +Math.sin(i * 0.7 + 1).toFixed(3));

export default function VectorCollections({ db, onChange }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", dim: 768, mode: "sq8" });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => api.get(`/db/${db}/vectors`).then((r) => {
    setItems(r.data.items);
    setSelected((s) => r.data.items.find((c) => c.name === s?.name) || r.data.items[0] || null);
  }).catch((e) => toast.error(errMsg(e))), [db]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/db/${db}/vectors`, { name: form.name.trim().toLowerCase(), dim: Number(form.dim), mode: form.mode });
      toast.success(`Coleção "${form.name}" criada`);
      setCreateOpen(false);
      setForm({ name: "", dim: 768, mode: "sq8" });
      load();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/db/${db}/vectors/${toDelete}`);
      toast.success(`Coleção "${toDelete}" excluída`);
      setToDelete(null);
      load();
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="vector-collections">
      <div className="flex items-center justify-between mb-4">
        <div className="overline-label">{items ? `${items.length} coleção(ões)` : ""}</div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-collection-button" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile"><Plus className="h-4 w-4 mr-1" /> Nova coleção</Button>
      </div>

      {!items ? (
        <LoadingRows />
      ) : items.length === 0 ? (
        <EmptyState icon={Layers} title="Nenhuma coleção vetorial" description="Crie uma coleção definindo a dimensão dos embeddings. O grafo HNSW é construído sob demanda e fica em disco (mmap)." testId="collections-empty"
          action={<Button onClick={() => setCreateOpen(true)} variant="outline" data-testid="collections-empty-create" className="rounded-sm border-zinc-700">Criar coleção</Button>} />
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          <div className="panel divide-y divide-zinc-800 self-start">
            {items.map((c) => (
              <button key={c.name} onClick={() => setSelected(c)} data-testid={`collection-item-${c.name}`}
                className={`w-full text-left px-4 py-3 transition-colors ${selected?.name === c.name ? "bg-zinc-800 border-l-2 border-orange-600" : "hover:bg-zinc-800/60 border-l-2 border-transparent"}`}>
                <div className="font-mono text-sm">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{c.dim}d · {c.mode} · {fmtNum((c.stats?.indexed || 0) + (c.stats?.pending || 0))} vetores</div>
              </button>
            ))}
          </div>
          {selected && <CollectionPanel key={selected.name} db={db} coll={selected} onChange={() => { load(); onChange?.(); }} onDelete={() => setToDelete(selected.name)} />}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-collection-dialog">
          <form onSubmit={create}>
            <DialogHeader>
              <DialogTitle className="font-display">Nova coleção vetorial</DialogTitle>
              <DialogDescription>Modo <b>sq8</b>: recall máximo. Modo <b>binary</b>: construção 5x mais rápida, exige ef maior.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required pattern="[a-z0-9_\-]{1,40}" placeholder="ex.: produtos" data-testid="collection-name-input" className="bg-transparent border-zinc-800 rounded-sm font-mono" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Dimensão</Label><Input type="number" min={1} max={8192} value={form.dim} onChange={(e) => setForm({ ...form, dim: e.target.value })} required data-testid="collection-dim-input" className="bg-transparent border-zinc-800 rounded-sm font-mono" /></div>
                <div className="space-y-2"><Label>Modo do grafo</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger data-testid="collection-mode-select" className="bg-transparent border-zinc-800 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800"><SelectItem value="sq8">sq8 (padrão)</SelectItem><SelectItem value="binary">binary</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} className="rounded-sm" data-testid="collection-create-cancel">Cancelar</Button>
              <Button type="submit" data-testid="collection-create-submit" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title={`Excluir coleção "${toDelete}"?`} description="Vetores, índice e payloads associados serão apagados." onConfirm={remove} testId="confirm-delete-collection" />
    </div>
  );
}

function CollectionPanel({ db, coll, onChange, onDelete }) {
  const s = coll.stats || {};
  const [upsert, setUpsert] = useState(() => JSON.stringify({ points: [{ id: 1, vector: sampleVector(Math.min(coll.dim, 8)), payload: { titulo: "exemplo" } }] }, null, 2));
  const [query, setQuery] = useState(() => JSON.stringify(sampleVector(Math.min(coll.dim, 8))));
  const [k, setK] = useState(5);
  const [ef, setEf] = useState(128);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const doUpsert = () => run("upsert", async () => {
    const body = JSON.parse(upsert);
    const { data } = await api.post(`/db/${db}/vectors/${coll.name}/points`, body);
    toast.success(`${data.upserted} ponto(s) inseridos em ${data.took_us} µs`);
    onChange();
  });

  const doBuild = () => run("build", async () => {
    const { data } = await api.post(`/db/${db}/vectors/${coll.name}/build`);
    toast.success(`Índice construído em ${data.took_ms} ms (${fmtNum(data.stats.indexed)} vetores)`);
    onChange();
  });

  const doSearch = () => run("search", async () => {
    const { data } = await api.post(`/db/${db}/vectors/${coll.name}/search`, { vector: JSON.parse(query), k: Number(k), ef: Number(ef) });
    setResult(data);
  });

  return (
    <div className="space-y-4" data-testid={`collection-panel-${coll.name}`}>
      <div className="panel p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-lg">{coll.name}</div>
            <div className="text-xs text-zinc-500">{coll.dim} dimensões · grafo {coll.mode} · criada em {new Date(coll.created_at).toLocaleString("pt-BR")}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={doBuild} disabled={!!busy} data-testid="collection-build-button" className="rounded-sm border-zinc-700 btn-tactile"><Hammer className="h-4 w-4 mr-1" /> {busy === "build" ? "Construindo…" : "Construir índice"}</Button>
            <Button size="sm" variant="ghost" onClick={onDelete} data-testid="collection-delete-button" className="rounded-sm text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 border border-zinc-800 divide-x divide-zinc-800 mt-4 text-center">
          {[["indexados", fmtNum(s.indexed || 0)], ["pendentes", fmtNum(s.pending || 0)], ["apagados", fmtNum(s.deleted || 0)], ["registros log", fmtNum(s.log_records || 0)], ["disco", fmtBytes(s.disk_bytes || 0)], ["heap", fmtBytes(s.heap_bytes || 0)]].map(([l, v]) => (
            <div key={l} className="py-2 border-b sm:border-b-0 border-zinc-800" data-testid={`collection-stat-${l.replace(" ", "-")}`}><div className="font-display font-bold">{v}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div></div>
          ))}
        </div>
        {s.pending > 0 && <p className="text-xs text-zinc-500 mt-3">Pendentes são buscados por força bruta até a próxima construção (automática a cada 20.000 inserções).</p>}
      </div>

      <div className="grid xl:grid-cols-2 gap-4">
        <div className="panel p-5 space-y-3">
          <div className="flex items-center gap-2 font-display font-semibold"><Upload className="h-4 w-4 text-orange-500" /> Inserir pontos</div>
          <Textarea rows={10} value={upsert} onChange={(e) => setUpsert(e.target.value)} data-testid="upsert-body-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-xs" />
          <Button onClick={doUpsert} disabled={!!busy} data-testid="upsert-submit-button" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">{busy === "upsert" ? "Enviando…" : "Enviar"}</Button>
        </div>
        <div className="panel p-5 space-y-3">
          <div className="flex items-center gap-2 font-display font-semibold"><Search className="h-4 w-4 text-orange-500" /> Testar busca</div>
          <Textarea rows={4} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="search-vector-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-xs" placeholder="[0.1, 0.2, ...]" />
          <div className="flex gap-3">
            <div className="space-y-1 flex-1"><Label className="text-xs">k</Label><Input type="number" min={1} max={1000} value={k} onChange={(e) => setK(e.target.value)} data-testid="search-k-input" className="bg-transparent border-zinc-800 rounded-sm font-mono h-9" /></div>
            <div className="space-y-1 flex-1"><Label className="text-xs">ef</Label><Input type="number" min={1} max={4096} value={ef} onChange={(e) => setEf(e.target.value)} data-testid="search-ef-input" className="bg-transparent border-zinc-800 rounded-sm font-mono h-9" /></div>
          </div>
          <Button onClick={doSearch} disabled={!!busy} variant="outline" data-testid="search-submit-button" className="rounded-sm border-zinc-700 btn-tactile">{busy === "search" ? "Buscando…" : "Buscar"}</Button>
          {result && (
            <div className="border border-zinc-800 rounded-sm" data-testid="search-results">
              <div className="px-3 py-2 text-xs text-zinc-500 border-b border-zinc-800 font-mono">{result.results.length} resultado(s) · {result.took_us} µs · ef={result.ef}</div>
              <div className="divide-y divide-zinc-800 max-h-64 overflow-auto">
                {result.results.map((r, i) => (
                  <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs font-mono row-enter" style={{ animationDelay: `${i * 30}ms` }} data-testid={`search-result-${r.id}`}>
                    <span className="text-orange-400">#{r.id}</span>
                    <span className="truncate text-zinc-400 flex-1">{r.payload ? JSON.stringify(r.payload) : "—"}</span>
                    <span className="text-white">{r.score.toFixed(4)}</span>
                  </div>
                ))}
                {result.results.length === 0 && <div className="px-3 py-4 text-xs text-zinc-500">Nenhum vetor na coleção.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
